import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Landmark, Plus, Trash2, Star, Link as LinkIcon, ExternalLink, Save, X, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Founder-only card that manages MULTIPLE bank accounts per founder plus
 * Stripe Financial Connections linking. Each founder can:
 *   • add / remove any number of accounts
 *   • flag one as primary (payroll source)
 *   • link any account to Stripe FC to enable live balance sync
 */
export default function FounderBankCard({ onChanged }) {
  const [rows, setRows] = useState(null);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [linkingId, setLinkingId] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null); // {id, name} — in-app confirm (window.confirm is blocked in the preview iframe)
  const [removing, setRemoving] = useState(false);
  const [form, setForm] = useState({
    bank_name: "", bank_routing: "", bank_account: "",
    bank_account_type: "checking", notes: "",
  });

  const load = async () => {
    try {
      const { data } = await api.get("/admin-auth/founder-banks");
      setRows(data.founders || []);
    } catch { toast.error("Could not load founder banks."); }
  };

  // Handle the redirect back from Stripe's hosted setup flow.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("founder_bank_linked") === "1" && q.get("session_id") && q.get("bank_id")) {
      const bankId = q.get("bank_id");
      const sid = q.get("session_id");
      api.post(`/admin-auth/founder-banks/${bankId}/link-complete?session_id=${encodeURIComponent(sid)}`)
        .then(() => {
          toast.success("Bank linked to Stripe. Live balance is now available.");
          onChanged?.();
        })
        .catch((e) => toast.error(e?.response?.data?.detail || "Could not complete link."))
        .finally(() => {
          // Clean up the URL so re-renders don't retry the completion.
          const url = new URL(window.location.href);
          ["founder_bank_linked", "session_id", "bank_id"].forEach((k) => url.searchParams.delete(k));
          window.history.replaceState({}, "", url.toString());
        });
    }
    load();
    // eslint-disable-next-line
  }, []);

  const submit = async () => {
    if (!/^\d{9}$/.test(form.bank_routing)) { toast.error("Routing must be 9 digits."); return; }
    if (!/^\d{4,17}$/.test(form.bank_account.replace(/-/g, ""))) { toast.error("Account must be 4–17 digits."); return; }
    setSaving(true);
    try {
      await api.post("/admin-auth/founder-banks", form);
      toast.success("Bank added.");
      setAdding(false);
      setForm({ bank_name: "", bank_routing: "", bank_account: "", bank_account_type: "checking", notes: "" });
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add."); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirmRemove) return;
    setRemoving(true);
    try {
      await api.delete(`/admin-auth/founder-banks/${confirmRemove.id}`);
      toast.success("Bank removed.");
      setConfirmRemove(null);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove."); }
    finally { setRemoving(false); }
  };

  const setPrimary = async (bankId) => {
    try {
      await api.post(`/admin-auth/founder-banks/${bankId}/set-primary`);
      toast.success("Primary account updated — payroll will draft from this one.");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update."); }
  };

  const linkStripe = async (bankId) => {
    setLinkingId(bankId);
    try {
      const { data } = await api.post(`/admin-auth/founder-banks/${bankId}/link-start`);
      // Redirect the current tab so Stripe can bounce back to /admin?founder_bank_linked=1&session_id=...
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Stripe link.");
      setLinkingId(null);
    }
  };

  if (rows === null) {
    return (
      <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5 flex items-center justify-center min-h-[120px]">
        <Loader2 className="h-4 w-4 animate-spin text-[#c9a961]" />
      </div>
    );
  }

  return (
    <div data-testid="founder-bank-card" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
      <div className="flex items-center gap-2 mb-4">
        <Landmark className="h-4 w-4 text-[#a3823f]" />
        <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Founder bank accounts</p>
        <span className="text-[9px] uppercase tracking-[0.22em] text-[#1e3d3a]/40">· Deposits + payroll source · Live balance via Stripe</span>
      </div>

      <ul className="space-y-4">
        {rows.map((row) => (
          <li key={row.founder_id} data-testid={`founder-bank-row-${row.username}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[13px] font-medium text-[#1e3d3a]">
                {row.display_name || row.username}
                {row.is_self && <span className="ml-2 text-[9px] uppercase tracking-[0.24em] text-[#a3823f]">You</span>}
              </p>
              {row.is_self && (
                <button
                  type="button"
                  data-testid={`founder-bank-add-${row.username}`}
                  onClick={() => setAdding(true)}
                  className="inline-flex items-center gap-1 rounded-full bg-[#1e3d3a] text-white px-3 py-1 text-[10px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300"
                >
                  <Plus className="h-3 w-3" /> Add bank
                </button>
              )}
            </div>

            {row.banks.length === 0 ? (
              <p className="text-[11px] text-[#1e3d3a]/45 italic pl-1">No bank accounts on file.</p>
            ) : (
              <ul className="divide-y divide-[#1e3d3a]/6 rounded-xl border border-[#1e3d3a]/8 bg-[#eae7de]/40 overflow-hidden">
                {row.banks.map((b) => (
                  <li key={b.id} data-testid={`founder-bank-account-${b.id}`} className="p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-[13px] font-medium text-[#1e3d3a]">{b.bank_name}</p>
                          {b.is_primary && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-[#c9a961]/20 text-[#a3823f] px-2 py-0.5 text-[9px] uppercase tracking-[0.22em]">
                              <Star className="h-2.5 w-2.5" /> Primary
                            </span>
                          )}
                          {b.fc_linked && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-[#6b7c3a]/15 text-[#4d5a24] px-2 py-0.5 text-[9px] uppercase tracking-[0.22em]">
                              <CheckCircle2 className="h-2.5 w-2.5" /> Stripe FC linked
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/50 mt-0.5">
                          Routing {b.bank_routing || "—"} · ••••{b.bank_last4 || "----"} · {b.bank_account_type}
                        </p>
                        {b.fc_bank_name && (
                          <p className="text-[9px] uppercase tracking-[0.18em] text-[#1e3d3a]/40 mt-0.5">
                            Stripe: {b.fc_bank_name} · ••••{b.fc_bank_last4}
                          </p>
                        )}
                      </div>
                      {row.is_self && (
                        <div className="flex items-center gap-1 shrink-0">
                          {!b.is_primary && (
                            <button
                              type="button"
                              data-testid={`bank-primary-${b.id}`}
                              onClick={() => setPrimary(b.id)}
                              title="Set as primary payroll source"
                              className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a]/70 px-2 py-1 text-[9px] uppercase tracking-[0.2em] hover:text-[#1e3d3a] hover:border-[#c9a961] transition-colors duration-300"
                            >
                              <Star className="h-2.5 w-2.5" /> Primary
                            </button>
                          )}
                          {!b.fc_linked ? (
                            <button
                              type="button"
                              data-testid={`bank-link-${b.id}`}
                              onClick={() => linkStripe(b.id)}
                              disabled={linkingId === b.id}
                              title="Link this account to Stripe for live balances"
                              className="inline-flex items-center gap-1 rounded-full bg-[#a3823f] text-white px-2.5 py-1 text-[9px] uppercase tracking-[0.2em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
                            >
                              {linkingId === b.id ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <LinkIcon className="h-2.5 w-2.5" />}
                              Link Stripe
                            </button>
                          ) : (
                            <a
                              href="https://dashboard.stripe.com/financial-connections/accounts"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 rounded-full border border-[#6b7c3a]/40 text-[#4d5a24] px-2 py-1 text-[9px] uppercase tracking-[0.2em] hover:bg-[#6b7c3a]/10 transition-colors duration-300"
                            >
                              <ExternalLink className="h-2.5 w-2.5" /> Stripe
                            </a>
                          )}
                          <button
                            type="button"
                            data-testid={`bank-remove-${b.id}`}
                            onClick={() => setConfirmRemove({ id: b.id, name: b.bank_name })}
                            className="inline-flex items-center gap-1 rounded-full border border-[#b23a48]/30 text-[#b23a48] px-2 py-1 text-[9px] uppercase tracking-[0.2em] hover:bg-[#b23a48]/10 transition-colors duration-300"
                          >
                            <Trash2 className="h-2.5 w-2.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>

      {adding && (
        <div data-testid="founder-bank-add-form" className="mt-4 grid grid-cols-2 gap-2 p-3 rounded-xl bg-[#eae7de]/60 border border-[#c9a961]/40">
          <Field label="Bank name" testid="fb-add-name" value={form.bank_name} onChange={(v)=>setForm({...form, bank_name:v})} className="col-span-2" />
          <Field label="Routing (9 digits)" testid="fb-add-routing" value={form.bank_routing} onChange={(v)=>setForm({...form, bank_routing:v.replace(/\D/g,"").slice(0,9)})} />
          <Field label="Account" testid="fb-add-account" value={form.bank_account} onChange={(v)=>setForm({...form, bank_account:v.replace(/[^0-9-]/g,"").slice(0,20)})} />
          <div>
            <label className="text-[9px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 mb-1 block">Type</label>
            <select
              data-testid="fb-add-type"
              value={form.bank_account_type}
              onChange={(e)=>setForm({...form, bank_account_type: e.target.value})}
              className="w-full rounded border border-[#1e3d3a]/15 bg-white px-2 py-1.5 text-sm"
            >
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
            </select>
          </div>
          <Field label="Notes (optional)" testid="fb-add-notes" value={form.notes} onChange={(v)=>setForm({...form, notes:v})} />
          <div className="col-span-2 flex justify-end gap-2 mt-1">
            <button type="button" data-testid="fb-add-cancel" onClick={() => setAdding(false)}
              className="text-[10px] uppercase tracking-[0.24em] text-[#1e3d3a]/55 hover:text-[#1e3d3a] px-3 py-1.5">
              Cancel
            </button>
            <button type="button" data-testid="fb-add-submit" onClick={submit} disabled={saving}
              className="inline-flex items-center gap-1 rounded-full bg-[#1e3d3a] text-white px-4 py-1.5 text-[10px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Add bank
            </button>
          </div>
        </div>
      )}

      {confirmRemove && (
        <div
          data-testid="bank-remove-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget && !removing) setConfirmRemove(null); }}
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#a3823f]">Bank · confirm</p>
            <h3 className="mt-1 font-head text-xl text-[#1e3d3a]">Remove this bank account?</h3>
            <p className="mt-2 text-sm text-[#1e3d3a]/70">
              “{confirmRemove.name}” will be removed from your founder bank accounts. This can’t be undone.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <button
                type="button"
                data-testid="bank-remove-confirm"
                onClick={remove}
                disabled={removing}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#b23a48] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#8f2a37] disabled:opacity-60 transition-colors duration-300"
              >
                {removing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Remove bank
              </button>
              <button
                type="button"
                onClick={() => setConfirmRemove(null)}
                disabled={removing}
                className="text-[11px] uppercase tracking-[0.22em] text-black/45 hover:text-[#1e3d3a] px-3 disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid, className = "" }) {
  return (
    <div className={className}>
      <label className="text-[9px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 mb-1 block">{label}</label>
      <input
        data-testid={testid}
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-[#1e3d3a]/15 bg-white px-2 py-1.5 text-sm focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
      />
    </div>
  );
}
